import Loadable from 'react-loadable';
import { match, NO_ELEMENT, rm, withCurrentPage } from 'utils';
import {
  getConfigurator,
  getAddons,
  isLawnMowerConfigurator,
  isPolarPumpConfigurator,
  isVariantConfigurator,
  isSlidingDoorConfigurator,
  isConfigurable,
  isVvsConfigurator,
  isZipCodeExcludeConfigurator,
  isKitchenContactConfigurator,
  isNotForSaleConfigurator,
  isShowerDoorConfigurator,
  isElfaConfigurator
} from './selector';
import { Reducer, useReducer, ReactElement, useCallback } from 'react';
import styles from './styles.scss';
import { VariantConfigurator as VariantConfiguratorModel } from 'models/VariantConfigurator';
import { ConfiguratorBase } from 'models/ConfiguratorBase';
import { getProductId, getIsFamily } from 'views/ProductPage/selector';
import { getVariantData } from 'views/ProductPage/actions';
import Modal from 'components/Shared/Modal';
import {
  PolarpumpInstallationConfigurator as PolarpumpInstallationConfiguratorModel,
  LawnMowerInstallationConfigurator as LawnMowerInstallationConfiguratorModel,
  VvsInstallationConfigurator as VvsInstallationConfiguratorModel,
  ZipCodeExcludeConfigurator as ZipCodeExcludeConfiguratorModel
} from 'models/InstallationConfigurator';
import { ReactComponent } from 'models/ReactComponent';
import Markdown, { isReactComponent } from 'components/Shared/Markdown';
import { SlidingDoorConfigurator as SlidingDoorConfiguratorModel } from 'models/SlidingDoorConfigurator';
import { ElfaConfigurator as ElfaConfiguratorModel } from 'models/ElfaConfigurator';
import { ConfigProvider } from 'views/ProductPage/configurators/useConfigurator';
import { ShowerDoorConfigurator as ShowerDoorConfiguratorModel } from 'models/ShowerDoorConfigurator';
import { isMobile } from 'components/Shared/responsive';
import { useDispatch, useSelector } from 'react-redux';
import { AddonGroup } from 'models/AddonGroup';
import { InstallationProps } from './installation';
import {
  configReset as configResetAction,
  onDeleteValue as onDeleteValueAction,
  onSelectValue as onSelectValueAction
} from './actions';
import { NotForSaleConfigurator } from './notforsale';

const VariantConfigurator = Loadable({
  loader: () =>
    import(
      /*webpackChunkName: "productpage"*/ /* webpackMode: "lazy" */ 'views/ProductPage/configurators/variant'
    ),
  loading: () => NO_ELEMENT,
  timeout: 1000
});

const SlidingDoorConfigurator = Loadable({
  loader: () =>
    import(
      /*webpackChunkName: "productpage"*/ /* webpackMode: "lazy" */ 'views/ProductPage/configurators/slidingDoor'
    ),
  loading: () => NO_ELEMENT,
  timeout: 1000
});

const ZipCodeExcludeConfigurator = Loadable({
  loader: () =>
    import(
      /*webpackChunkName: "productpage"*/ /* webpackMode: "lazy" */ 'views/ProductPage/configurators/installation/zipcodeexclude'
    ),
  loading: () => NO_ELEMENT,
  timeout: 1000
});

const ElfaConfigurator = Loadable({
  loader: () =>
    import(
      /*webpackChunkName: "productpage"*/ /* webpackMode: "lazy" */ 'views/ProductPage/configurators/elfa'
    ),
  loading: () => NO_ELEMENT,
  timeout: 1000
});

const PolarpumpConfigurator = Loadable({
  loader: () =>
    import(
      /*webpackChunkName: "productpage"*/ /* webpackMode: "lazy" */ 'views/ProductPage/configurators/installation/polarpump'
    ),
  loading: () => NO_ELEMENT,
  timeout: 1000
});

const InstallationConfigurator = Loadable({
  loader: () =>
    import(
      /*webpackChunkName: "productpage"*/ /* webpackMode: "lazy" */ 'views/ProductPage/configurators/installation'
    ),
  loading: () => NO_ELEMENT,
  timeout: 1000
});

const KitchenContactConfigurator = Loadable({
  loader: () =>
    import(
      /*webpackChunkName: "productpage"*/ /* webpackMode: "lazy" */ 'views/ProductPage/configurators/kitchencontact'
    ),
  loading: () => NO_ELEMENT,
  timeout: 1000
});

const ShowerDoorConfigurator = Loadable({
  loader: () =>
    import(
      /*webpackChunkName: "productpage"*/ /* webpackMode: "lazy" */ 'views/ProductPage/configurators/showerdoor'
    ),
  loading: () => NO_ELEMENT,
  timeout: 1000
});

export interface ConfiguratorProps {
  onVariant?: (variant?: string) => void;
  onSelectValue: (id: string, value?: string) => void;
  isMobile?: boolean;
  inDrawer?: boolean;
}

interface MergedProps extends ConfiguratorProps, Partial<InstallationProps> {
  configReset: () => void;
  configurator: ConfiguratorBase;
  isFamily: boolean;
  isConfigurable: boolean;
  addons: { [key: string]: AddonGroup };
}

export interface ModalProps {
  setModal: ConfigModal['setModal'];
  reset: (args?: { showModal?: boolean }) => void;
}

const useConfigReset = () => {
  const dispatch = useDispatch();
  return () => dispatch(configResetAction());
};

const useVariant = () => {
  const dispatch = useDispatch();
  return (product: string, variant: string) => dispatch(getVariantData(product, variant));
};

const useConfiguratorState = () => {
  const dispatch = useDispatch();
  const onSelectValue = useCallback(
    (id: string, value: string) =>
      dispatch(value === undefined ? onDeleteValueAction(id) : onSelectValueAction(id, value)),
    [dispatch]
  );
  const configReset = useConfigReset();
  const isPhone = useSelector(isMobile);
  const productId = useSelector(withCurrentPage(getProductId));
  const configurator = useSelector(withCurrentPage(getConfigurator));
  const isFamily = useSelector(withCurrentPage(getIsFamily));
  const isProductConfigurable = useSelector(withCurrentPage(isConfigurable));
  const addons = useSelector(withCurrentPage(getAddons));

  const getVariant = useVariant();
  const onVariant = (id: string) => getVariant(productId, id);

  return {
    onSelectValue,
    configReset,
    isMobile: isPhone,
    configurator,
    isFamily,
    isConfigurable: isProductConfigurable,
    addons: addons ?? {},
    onVariant
  };
};

export const StandaloneInstallation = () => {
  const configurator = useConfiguratorState();
  return <Installation {...configurator} />;
};

export const Installation = ({ configurator, isConfigurable, isFamily, ...props }: MergedProps) => {
  const [modal, setModal] = useModal();

  if (!isConfigurable) {
    return NO_ELEMENT;
  }

  const reset = (args = { showModal: true }) => {
    args.showModal && setModal(rm('product_variant_nomatch'));
    props.configReset();
  };
  const onClose = () => setModal(undefined);

  return (
    <>
      <Modal
        contentClassName={styles.configuratorModal}
        show={modal.show}
        onClose={onClose}
        showDefaultButton
      >
        <ul className={styles.messages}>
          {modal.messages.map((message, i) => (
            <li key={i}>{isReactComponent(message) ? <Markdown {...message} /> : message}</li>
          ))}
        </ul>
      </Modal>
      {match<ConfiguratorBase, ReactElement | null>(configurator)
        .when<LawnMowerInstallationConfiguratorModel | VvsInstallationConfiguratorModel>(
          config => isLawnMowerConfigurator(config) || isVvsConfigurator(config),
          config => (
            <InstallationConfigurator
              {...{
                configurator: config,
                ...props,
                reset,
                setModal,
                showInstallation: !isFamily
              }}
            />
          )
        )
        .when<PolarpumpInstallationConfiguratorModel>(isPolarPumpConfigurator, config => (
          <PolarpumpConfigurator
            {...{
              configurator: config,
              ...props,
              reset,
              setModal,
              showInstallation: !isFamily
            }}
          />
        ))
        .or(NO_ELEMENT)
        .do(configurator)}
    </>
  );
};

export const StandaloneConfigurator = () => {
  const configurator = useConfiguratorState();
  return <Configurator {...configurator} />;
};

export const Configurator = ({
  configurator,
  inDrawer,
  addons,
  isFamily,
  isConfigurable,
  ...rest
}: MergedProps) => {
  const [modal, setModal] = useModal();

  if (!isConfigurable) {
    return NO_ELEMENT;
  }

  const reset = (args = { showModal: true }) => {
    args.showModal && setModal(rm('product_variant_nomatch'));
    rest.configReset();
  };

  const onClose = () => setModal(undefined);
  return (
    <>
      <Modal
        contentClassName={styles.configuratorModal}
        show={modal.show}
        onClose={onClose}
        showDefaultButton
      >
        <ul className={styles.messages}>
          {modal.messages.map((message, i) => (
            <li key={i}>{isReactComponent(message) ? <Markdown {...message} /> : message}</li>
          ))}
        </ul>
      </Modal>
      {match<ConfiguratorBase, ReactElement | null>(configurator)
        .when<VariantConfiguratorModel>(isVariantConfigurator, config => (
          <ConfigProvider
            {...{
              inDrawer,
              configurator,
              reset,
              setModal,
              ...rest
            }}
          >
            <VariantConfigurator {...{ configurator: config, ...rest, reset, setModal }} />
          </ConfigProvider>
        ))
        .when<SlidingDoorConfiguratorModel>(isSlidingDoorConfigurator, config => (
          <ConfigProvider
            {...{
              inDrawer,
              configurator,
              reset,
              setModal,
              ...rest
            }}
          >
            <SlidingDoorConfigurator {...{ configurator: config, ...rest, reset, setModal }} />
          </ConfigProvider>
        ))
        .when<ZipCodeExcludeConfiguratorModel>(isZipCodeExcludeConfigurator, config => (
          <>
            {config.variants && (
              <ConfigProvider
                {...{
                  inDrawer,
                  configurator,
                  reset,
                  setModal,
                  ...rest
                }}
              >
                <VariantConfigurator {...{ configurator: config, ...rest, reset, setModal }} />
              </ConfigProvider>
            )}
            <ZipCodeExcludeConfigurator {...{ configurator: config, ...rest, reset, setModal }} />
          </>
        ))
        .when<ShowerDoorConfiguratorModel>(isShowerDoorConfigurator, config => (
          <>
            {config.variants && (
              <ConfigProvider
                {...{
                  inDrawer,
                  configurator,
                  reset,
                  setModal,
                  ...rest
                }}
              >
                <ShowerDoorConfigurator {...{ configurator: config, ...rest, reset, setModal }} />
              </ConfigProvider>
            )}
          </>
        ))
        .when<LawnMowerInstallationConfiguratorModel | VvsInstallationConfiguratorModel>(
          config => isLawnMowerConfigurator(config) || isVvsConfigurator(config),
          config => (
            <>
              {config.variants && (
                <ConfigProvider
                  {...{
                    inDrawer,
                    configurator,
                    reset,
                    setModal,
                    ...rest
                  }}
                >
                  <VariantConfigurator {...{ configurator: config, ...rest, reset, setModal }} />
                </ConfigProvider>
              )}
            </>
          )
        )
        .when<PolarpumpInstallationConfiguratorModel>(isPolarPumpConfigurator, config => (
          <>
            {config.variants && (
              <ConfigProvider
                {...{
                  inDrawer,
                  configurator,
                  reset,
                  setModal,
                  ...rest
                }}
              >
                <VariantConfigurator {...{ configurator: config, ...rest, reset, setModal }} />
              </ConfigProvider>
            )}
          </>
        ))
        .when(isKitchenContactConfigurator, () => (
          <KitchenContactConfigurator setModal={setModal} />
        ))
        .when(isNotForSaleConfigurator, () => <NotForSaleConfigurator />)
        .when<ElfaConfiguratorModel>(isElfaConfigurator, config => (
          <ConfigProvider
            {...{
              inDrawer,
              configurator,
              reset,
              setModal,
              ...rest
            }}
          >
            <ElfaConfigurator
              {...{
                configurator: config,
                ...rest,
                reset,
                setModal,
                addons,
                isFamily
              }}
            />
          </ConfigProvider>
        ))
        .or(NO_ELEMENT)
        .do(configurator)}
    </>
  );
};

type ModalMessage = string | ReactComponent;
interface ConfigModal {
  state: { show: boolean; messages: ModalMessage[] };
  setModal: (message?: ModalMessage | ModalMessage[]) => void;
}
interface ModalAction {
  messages: ModalMessage[];
  type: string;
}
type ModalReducer = Reducer<ConfigModal['state'], ModalAction>;
const MODAL_SET = 'MODAL_SET';
const MODAL_CLOSE = 'MODAL_CLOSE';
const reducer: ModalReducer = (state, { type, messages }) => {
  switch (type) {
    case MODAL_SET:
      return { show: true, messages };
    case MODAL_CLOSE:
      return { show: false, messages: [] };
    default:
      return state;
  }
};

function useModal(): [ConfigModal['state'], ConfigModal['setModal']] {
  const [state, dispatch] = useReducer<ModalReducer>(reducer, {
    show: false,
    messages: []
  });
  const setModal: ConfigModal['setModal'] = (messages = []) => {
    const msg = Array.isArray(messages) ? messages : [messages];
    dispatch({
      type: msg.length > 0 ? MODAL_SET : MODAL_CLOSE,
      messages: msg
    });
  };
  return [state, setModal];
}
